import { Injectable } from '@angular/core';
import { MapUtilsService } from '../map/map-utils.service';
import { GMapsManager } from './GMapsManager';
import { UtilsGeo } from './UtilsGeo';
import { CreationSegmentsType, FeatureProperty, FeatureType, GeometryType } from 'src/app/models/enums';
import { Properties } from 'src/app/models';
import { UtilsService } from '../utils/utils.service';

@Injectable({
  providedIn: 'root'
})
export class CreateOptions {

  public map: google.maps.Map;
  public FeatureType = google.maps.FeatureType;
  public minId = 0;

  public gmaps = new GMapsManager();
  public utilsGeo = new UtilsGeo();
  
  constructor(private mapUtilsService: MapUtilsService, private utilsService: UtilsService) {}

  setMinId(minId: number) {
    this.minId = minId;
  }

  async addSegment(map: google.maps.Map, from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral, origin: Properties, destination: Properties, somePoints: { latitude: number, longitude: number }[] = [], creationType: CreationSegmentsType, drivingSpeed: number) {
    this.map = map;
    let path: any = [];
    let segmentDistance: any = 0;
    let segmentWeight: any = 0;
    if (creationType === CreationSegmentsType.AUTO) {
      const data: any = await this.mapUtilsService.computeRoutes(from, to, somePoints);
      if (!data?.routes?.[0]) throw new Error('No route found between the selected stops');
      data.routes[0].legs.forEach((leg: any) => {
        const encodedPolyline = leg.polyline.encodedPolyline;
        path = path.concat(google.maps.geometry.encoding.decodePath(encodedPolyline));
        segmentDistance += leg.distanceMeters;
        segmentWeight += Number(leg.duration.replace('s', ''));
      });
    } else {
      path = somePoints.map(p => new google.maps.LatLng(p.latitude, p.longitude));
      const totalPath = [new google.maps.LatLng(from.lat, from.lng), ...path, new google.maps.LatLng(to.lat, to.lng)];
      segmentDistance = this.utilsGeo.calculateSegmentDistance({
        geometry: {
          coordinates: totalPath.map(p => [p.lng(), p.lat()])
        }
      });
      segmentWeight = this.utilsGeo.calculateSegmentDuration(segmentDistance, drivingSpeed);
    }
    const result = this.buildServiceSegmentFeature(path, segmentDistance, segmentWeight, origin, destination)
    map.data.overrideStyle(result, { strokeColor: '#280a54', strokeWeight: 6 });
    return map.data.add(result);
  }

  buildServiceSegmentFeature(path: google.maps.LatLng[], segmentDistance: any, segmentWeight: any, origin: Properties, destination: Properties) {
    let coordinates = [
      {lat: origin.geometry?.coordinates.lat, lng: origin.geometry?.coordinates.lng},
      ...path.map(p => ({lat: p.lat(), lng: p.lng()})),
      {lat: destination.geometry?.coordinates.lat, lng: destination.geometry?.coordinates.lng}
    ];
    
    this.minId = this.minId - 1;
    const segmentFeature = this.buildSegmentFeature(this.minId, coordinates);

    segmentFeature.geometry = new google.maps.Data.LineString(coordinates);
    segmentFeature.properties.start = this.buildSegmentPoint(origin);
    segmentFeature.properties.end   = this.buildSegmentPoint(destination);
    segmentFeature.properties.weight = segmentWeight;
    segmentFeature.properties.distance = (segmentDistance / 1000).toFixed(4);
    segmentFeature.properties.type = FeatureType.SEGMENT;
    segmentFeature.properties.formattedTime = this.utilsService.convertSecondsToMinutesAndSeconds(segmentWeight, false);
    segmentFeature.id = this.minId;

    const polyline = this.gmaps.addPolyline(
      coordinates.map(this.utilsGeo.toGoogleLatLng),
      "#FFF45F",
      segmentFeature,
      { draggableVertex: false }
    );

    polyline.setOptions({ zIndex: 1000 });
    segmentFeature.properties.polyline = polyline;
    
    return segmentFeature;
  }

  private buildSegmentPoint(pointFeature: any): Record<string, any> {
    const point = pointFeature.geometry;
    const stopGeometryMap = {
      [FeatureProperty.TYPE]: point.type,
      [FeatureProperty.COORDINATES]: [point.coordinates.lng, point.coordinates.lat]
    };

    return {
      [FeatureProperty.TYPE]: "Feature",
      [FeatureProperty.PROPERTIES]: pointFeature.properties,
      [FeatureProperty.GEOMETRY]: stopGeometryMap
    };
  }

  buildSegmentFeature(segmentId: number, coordinates: any[]) {
    let segment: any = {
      [FeatureProperty.TYPE]: 'Feature',
      [FeatureProperty.GEOMETRY]: {
        [FeatureProperty.TYPE]: 'LineString',
        [FeatureProperty.COORDINATES]: coordinates
      },
      [FeatureProperty.PROPERTIES]: {},
      [FeatureProperty.ID]: segmentId
    }
    segment[FeatureProperty.PROPERTIES][FeatureProperty.TYPE] = FeatureType.SEGMENT;
    segment[FeatureProperty.PROPERTIES][FeatureProperty.ID] = segmentId;
    return segment;
  }

  addStop(map: google.maps.Map, coordinates: {lat: number, lng: number}, radius: number, name: string, type: FeatureType = FeatureType.STOP) {
    this.map = map;

    this.minId = this.minId - 1;
    const pointFeature = this.buildPointFeature(this.minId, coordinates, { type });
    pointFeature[FeatureProperty.GEOMETRY] = new google.maps.Data.Point(coordinates);
    pointFeature[FeatureProperty.PROPERTIES].bidirectional = null;
    pointFeature[FeatureProperty.PROPERTIES].demand = false;
    pointFeature[FeatureProperty.PROPERTIES].direction = 0;
    pointFeature[FeatureProperty.PROPERTIES].end = false;
    pointFeature[FeatureProperty.PROPERTIES].inflexion = false;
    pointFeature[FeatureProperty.PROPERTIES].merge = null;
    pointFeature[FeatureProperty.PROPERTIES].name = name;
    pointFeature[FeatureProperty.PROPERTIES].radius = radius;
    pointFeature[FeatureProperty.PROPERTIES].start = false;
    pointFeature[FeatureProperty.ID] = this.minId;

    const feature = new google.maps.Data.Feature(pointFeature);
    return map.data.add(feature);
  }

  buildPointFeature(pointId: number, coordinates: {lat: number, lng: number}, properties: any = {}) {
    let point: any = {
      [FeatureProperty.TYPE]: 'Feature',
      [FeatureProperty.GEOMETRY]: {
        [FeatureProperty.TYPE]: GeometryType.POINT,
        [FeatureProperty.COORDINATES]: coordinates
      },
      [FeatureProperty.PROPERTIES]: properties,
      [FeatureProperty.ID]: pointId
    }
    point[FeatureProperty.PROPERTIES][FeatureProperty.TYPE] = properties.type || FeatureType.STOP;
    point[FeatureProperty.PROPERTIES][FeatureProperty.ID] = pointId;
    return point;
  }

  buildAreaFeature(map: google.maps.Map, coordinates: {lat: number, lng: number}[], featureId: string, name: string, color?: string) {
    this.map = map;
    const feature = new google.maps.Data.Feature({
      geometry: new google.maps.Data.Polygon([coordinates]),
      id: featureId,
      properties: {
        [FeatureProperty.TYPE]: FeatureType.OUTLINE,
        [FeatureProperty.ID]: featureId,
        [FeatureProperty.AREA_ID]: null,
        [FeatureProperty.NAME]: name,
        ...(color ? { [FeatureProperty.OUTLINE_COLOR]: color } : {})
      }
    });
    const addedFeature = map.data.add(feature);
    if (color) {
      map.data.overrideStyle(addedFeature, {
        strokeColor: color,
        strokeWeight: 2,
        strokeOpacity: 1,
        fillColor: color,
        fillOpacity: 0.10
      });
    }
    return addedFeature;
  }

  buildMergedFeature(stop: any) {
    this.minId = this.minId - 1;
    return {
      [FeatureProperty.TYPE]: 'Feature',
      [FeatureProperty.GEOMETRY]: {
        [FeatureProperty.TYPE]: GeometryType.POINT,
        [FeatureProperty.COORDINATES]: [stop.lng, stop.lat]
      },
      [FeatureProperty.PROPERTIES]: {
        [FeatureProperty.ID]: this.minId,
        [FeatureProperty.NAME]: stop.name,
        [FeatureProperty.TYPE]: FeatureType.STOP
      }
    }
  }
  
}
